import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReactNode, memo, useState } from 'react';
import { Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import { motion } from 'framer-motion';

import { AddIcon, GridIcon } from '@treams/treams-icons';
import { Card, IconButton, Paragraph, Spinner } from '@treams/treams-ui';

import { LibraryRenderCard } from './LibraryRenderer';

const Container = styled(Flex)<{ loading?: boolean; dragging?: boolean; hover?: boolean }>`
	border-radius: 5px;
	transition: 0.2s ease-in-out background-color;

	${(props) =>
		!props.loading &&
		(props.dragging
			? `background: ${props.theme.colors.lightBlue};`
			: props.hover && `background: ${rgba(props.theme.colors.lightBlue, 0.5)};`)};
`;

const Handle = styled(Flex)<{ loading?: boolean; dragging?: boolean }>`
	${(props) => !props.loading && !props.dragging && `cursor: grab;`}
	width: 40px;

	svg {
		height: 24px;
		width: 24px;

		path {
			fill: ${(props) => props.theme.colors.lightBlueDark};
		}
	}
`;

const PlaceholderContainer = styled(Flex)`
	border: 1px dashed ${(props) => props.theme.colors.lightBlueDark};
	min-height: 94px;
	border-radius: 5px;
	cursor: pointer;
	transition:
		0.2s ease-in-out border-color,
		0.2s ease-in-out background-color;

	&:hover {
		border-color: transparent;
		background: ${(props) => props.theme.colors.lightBlueLight};

		&:active {
			background: ${(props) => props.theme.colors.lightBlue};
		}
	}
`;

type LibraryItemProps<U> = {
	active?: boolean;
	item: U;
	query?: string;
	loading?: boolean;
	selected?: boolean;
	new?: boolean;
	dragging?: boolean;
	draggable?: boolean;
	readOnly?: boolean;
	handle?: object;
	handleIcon?: ReactNode;
	buttons?: ReactNode[];
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: U) => void;
	onUpdate?: (item: U) => void;
	onSelect?: (item: U) => void;
	onForget?: (item: U) => void;
	renderCard: LibraryRenderCard<U>;
};

export function LibraryItem<U extends { id: string }>(props: LibraryItemProps<U>) {
	const loading = props.loading || (props.dragging && !props.active);

	const [hover, setHover] = useState(false);

	return (
		<Container
			loading={loading}
			dragging={props.dragging}
			hover={hover}
			pl={props.draggable ?? true ? 0 : 2}
		>
			{(props.draggable ?? true) && (
				<Handle
					{...(loading ? {} : props.handle)}
					loading={loading}
					dragging={props.dragging}
					flexGrow={0}
					flexShrink={0}
					px={2}
					alignItems="center"
					justifyContent="center"
					onMouseEnter={() => setHover(true)}
					onMouseLeave={() => setHover(false)}
				>
					{loading ? <Spinner /> : props.handleIcon ?? <GridIcon />}
				</Handle>
			)}
			{props.renderCard(props)}
		</Container>
	);
}

LibraryItem.Memo = memo(LibraryItem) as unknown as typeof LibraryItem;

function LibraryItemDraggable<U extends { id: string }>(props: LibraryItemProps<U>) {
	const { active } = useDndContext();
	const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
		id: props.item.id ? `item:${props.item.id}` : '$new',
		disabled: (active?.id as string)?.startsWith('category:'),
	});

	const animated = !active?.id.toString().startsWith('category:');

	return (
		<motion.div
			layoutId={animated ? `item:${props.item.id}` : undefined}
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0 : 1,
			}}
			{...attributes}
			transition={{ duration: 0.2, ease: 'circOut' }}
		>
			<LibraryItem.Memo active={!!active} handle={listeners} {...props} />
		</motion.div>
	);
}

LibraryItem.Draggable = LibraryItemDraggable;

function LibraryItemDragOverlay<U extends { id: string }>(props: LibraryItemProps<U>) {
	const { active } = useDndContext();

	return <LibraryItem.Memo active={!!active} {...props} />;
}

LibraryItem.DragOverlay = memo(LibraryItemDragOverlay) as unknown as typeof LibraryItemDragOverlay;

function LibraryItemPlaceholder(props: { categoryID: string; onClick?: () => void }) {
	const { active } = useDndContext();
	const { attributes, setNodeRef, transform, isDragging } = useSortable({
		id: `$placeholder:${props.categoryID}`,
		disabled: true,
	});

	const animated = !!active?.id.toString().startsWith('item:');

	return (
		<motion.div
			layoutId={animated ? `$placeholder:${props.categoryID}` : undefined}
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(transform),
				opacity: isDragging ? 0 : 1,
			}}
			{...attributes}
			transition={{ duration: 0.2, ease: 'circOut' }}
		>
			<PlaceholderContainer
				alignItems="center"
				justifyContent="center"
				ml={40}
				onClick={props.onClick}
			>
				<IconButton type="secondary" icon={<AddIcon />} />
			</PlaceholderContainer>
		</motion.div>
	);
}

LibraryItem.Placeholder = LibraryItemPlaceholder;

function LibraryItemSkeleton(props: { draggable?: boolean }) {
	return (
		<Card.Skeleton ml={props.draggable === false ? 0 : 40}>
			<Paragraph.Skeleton lines={2} />
		</Card.Skeleton>
	);
}

LibraryItem.Skeleton = LibraryItemSkeleton;
